import { Component, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { WhatsappSettingsService, WhatsAppUpdateConnectionSettingsPayload, WahaLogLevel, WhatsAppVolumeSize, WHATSAPP_VOLUME_SIZES, whatsAppVolumeSizeRank, WAHA_IMAGE_SOURCES, DEFAULT_WAHA_IMAGE_PREFIX, resolveWahaImagePrefix, extractWahaImageTag, wahaImageSourcesIncluding } from '../../services/whatsapp-settings.service';
import NiboWhatsAppConnection from '../../models/nibo-whatsapp-connection';
import { environment } from 'src/environments/environment';
import { IncomingNiboWhatsAppData } from '../../models/incoming-nibo-whatsapp-data';

@Component({
  standalone: false,
  selector: 'app-connection-settings',
  templateUrl: './connection-settings.component.html',
  styleUrls: ['./connection-settings.component.css']
})
export class ConnectionSettingsComponent implements OnInit {
  public form!: FormGroup;
  public connectionId = '';
  public readonly saving = signal(false);
  public readonly loading = signal(true);

  // Ganha a origem da própria conexão quando ela roda um repositório fora da lista conhecida.
  public readonly wahaImageSources = signal<string[]>([...WAHA_IMAGE_SOURCES]);

  // Imagens que os pods da conexão estão rodando de fato. O formulário mostra o estado
  // desejado (banco), e entre salvar e o Argo CD reconciliar os dois divergem — daí o caption.
  // Pode haver mais de uma: o endpoint devolve uma entrada por réplica e, durante um rollout,
  // as réplicas podem estar em imagens diferentes.
  public readonly runningWahaImages = signal<string[]>([]);
  public readonly runningNiboWahaImages = signal<string[]>([]);
  public readonly niboWahaImagePrefix: string;

  public readonly logLevels: WahaLogLevel[] = ['warn', 'info'];
  public readonly volumeSizes: WhatsAppVolumeSize[] = WHATSAPP_VOLUME_SIZES;

  // Tamanho de disco atual da conexão. O Kubernetes só permite aumentar, nunca reduzir.
  public readonly currentVolumeSize = signal<WhatsAppVolumeSize>('4Gi');

  constructor(
    private readonly httpClient: HttpClient,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    private readonly settingsService: WhatsappSettingsService
  ) {
    this.niboWahaImagePrefix = this.settingsService.niboWahaImagePrefix;
  }

  ngOnInit(): void {
    this.connectionId = (this.route.snapshot.paramMap.get('connectionId') ?? '').trim();

    this.form = this.formBuilder.group({
      wahaImageSource: [DEFAULT_WAHA_IMAGE_PREFIX, [Validators.required]],
      wahaImageTag: ['', [Validators.required]],
      wahaLogLevel: ['warn', [Validators.required]],
      wahaEnableTagEvents: [false],
      niboWahaImageTag: ['', [Validators.required]],
      niboWahaChartVersion: ['', [Validators.required]],
      vpaConfigWAHAMinAllowedMemory: ['', [Validators.required]],
      vpaConfigWAHAMaxAllowedMemory: ['', [Validators.required]],
      volumeSize: ['4Gi', [Validators.required]]
    });

    this.prefillFromCurrentConnection();
  }

  private prefillFromCurrentConnection(): void {
    if (!this.connectionId) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.httpClient.get<IncomingNiboWhatsAppData[]>(
      `${environment.whatsAppApi}/infrastructure/connections`
    ).pipe(take(1)).subscribe({
      next: (data) => {
        const connections = (data ?? []).map(d => new NiboWhatsAppConnection(d));
        // Uma entrada por pod: a config é a mesma em todas, mas as imagens em execução não.
        const entries = connections.filter(c => c.connectionId === this.connectionId);
        const connection = entries[0];
        if (!connection) {
          return;
        }

        this.runningWahaImages.set(this.distinctImages(entries, c => c.pod?.wahaContainer?.image));
        this.runningNiboWahaImages.set(this.distinctImages(entries, c => c.pod?.niboWAHAContainer?.image));

        // A imagem configurada tem precedencia sobre a que o pod esta rodando: entre salvar e o
        // rollout acontecer as duas divergem, e conexao sem pod so tem a configurada.
        const wahaImage = connection.wahaImage || connection.pod?.wahaContainer?.image;
        const wahaImagePrefix = resolveWahaImagePrefix(wahaImage);
        const wahaImageTag = extractWahaImageTag(wahaImage);
        this.wahaImageSources.set(wahaImageSourcesIncluding(wahaImagePrefix));
        const niboImage = connection.niboWAHAImage || connection.pod?.niboWAHAContainer?.image;
        const niboImageTagFromImage = this.extractImageTag(niboImage, this.niboWahaImagePrefix);
        const niboImageTag = niboImageTagFromImage || (connection.niboWAHAVersion ?? '');
        const nextLogLevel = this.normalizeLogLevel(connection.wahaLogLevel);

        this.form.patchValue({
          wahaImageSource: wahaImagePrefix,
          wahaImageTag: wahaImageTag,
          wahaLogLevel: nextLogLevel,
          wahaEnableTagEvents: !!connection.wahaEnableTagEvents,
          niboWahaImageTag: niboImageTag,
          niboWahaChartVersion: connection.niboWAHAChartVersion ?? '',
          vpaConfigWAHAMinAllowedMemory: connection.vpaConfigWAHAMinAllowedMemory ?? '',
          vpaConfigWAHAMaxAllowedMemory: connection.vpaConfigWAHAMaxAllowedMemory ?? '',
          volumeSize: this.normalizeVolumeSize(connection.volumeSize)
        }, { emitEvent: false });

        this.currentVolumeSize.set(this.normalizeVolumeSize(connection.volumeSize));
      },
      error: () => {
        // silêncio: a tela ainda permite preenchimento manual
      }
    }).add(() => {
      this.loading.set(false);
    });
  }

  private extractImageTag(image: string | undefined, expectedPrefix: string): string {
    const value = String(image ?? '').trim();
    if (!value) {
      return '';
    }

    if (value.startsWith(expectedPrefix)) {
      return value.substring(expectedPrefix.length).trim();
    }

    const parts = value.split(':').filter(p => p.length > 0);
    return parts.length > 1 ? parts[parts.length - 1].trim() : '';
  }

  private normalizeLogLevel(value: string | undefined): WahaLogLevel {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'info' ? 'info' : 'warn';
  }

  private normalizeVolumeSize(value: string | undefined): WhatsAppVolumeSize {
    const normalized = String(value ?? '').trim();
    return this.volumeSizes.includes(normalized as WhatsAppVolumeSize)
      ? (normalized as WhatsAppVolumeSize)
      : '4Gi';
  }

  /** Imagens distintas em execução, na ordem em que os pods aparecem. */
  private distinctImages(
    entries: NiboWhatsAppConnection[],
    pick: (connection: NiboWhatsAppConnection) => string | undefined
  ): string[] {
    const images = entries
      .map(entry => String(pick(entry) ?? '').trim())
      .filter(image => image.length > 0);

    return Array.from(new Set(images));
  }

  /** Nome completo montado no formulário — o estado desejado, que será enviado ao salvar. */
  private configuredImage(sourceControl: string, tagControl: string): string {
    const prefix = String(this.form?.value?.[sourceControl] ?? '');
    const tag = String(this.form?.value?.[tagControl] ?? '').trim();
    return `${prefix}${tag}`;
  }

  /** Algum pod roda uma imagem do WAHA diferente da configurada. */
  public isWahaImageOutOfSync(): boolean {
    const configured = this.configuredImage('wahaImageSource', 'wahaImageTag');
    return this.runningWahaImages().some(image => image !== configured);
  }

  /** Algum pod roda uma imagem do Nibo WAHA diferente da configurada. */
  public isNiboWahaImageOutOfSync(): boolean {
    const configured = `${this.niboWahaImagePrefix}${String(this.form?.value?.niboWahaImageTag ?? '').trim()}`;
    return this.runningNiboWahaImages().some(image => image !== configured);
  }

  /** Impede selecionar um disco menor que o atual — Kubernetes só permite expandir PVC. */
  public isVolumeSizeDisabled(size: WhatsAppVolumeSize): boolean {
    return whatsAppVolumeSizeRank(size) < whatsAppVolumeSizeRank(this.currentVolumeSize());
  }

  public save(): void {
    if (this.saving()) {
      return;
    }

    if (!this.connectionId) {
      this.toastrService.error('ConnectionId inválido');
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const volumeSize = this.normalizeVolumeSize(this.form.value.volumeSize);
    if (whatsAppVolumeSizeRank(volumeSize) < whatsAppVolumeSizeRank(this.currentVolumeSize())) {
      this.toastrService.error(`Não é possível reduzir o disco de ${this.currentVolumeSize()} para ${volumeSize}. O Kubernetes só permite aumentar.`);
      return;
    }

    const wahaImagePrefix = String(this.form.value.wahaImageSource ?? DEFAULT_WAHA_IMAGE_PREFIX);
    const wahaImageTag = String(this.form.value.wahaImageTag ?? '').trim();
    const niboWahaImageTag = String(this.form.value.niboWahaImageTag ?? '').trim();

    const payload: WhatsAppUpdateConnectionSettingsPayload = {
      connectionId: this.connectionId,
      wahaImage: `${wahaImagePrefix}${wahaImageTag}`,
      wahaLogLevel: this.form.value.wahaLogLevel,
      wahaEnableTagEvents: !!this.form.value.wahaEnableTagEvents,
      niboWahaImage: `${this.niboWahaImagePrefix}${niboWahaImageTag}`,
      niboWahaChartVersion: String(this.form.value.niboWahaChartVersion ?? '').trim(),
      vpaConfigWAHAMinAllowedMemory: String(this.form.value.vpaConfigWAHAMinAllowedMemory ?? '').trim(),
      vpaConfigWAHAMaxAllowedMemory: String(this.form.value.vpaConfigWAHAMaxAllowedMemory ?? '').trim(),
      volumeSize: volumeSize
    };
    this.saving.set(true);

    this.settingsService.updateConnection(payload).subscribe({
      next: () => {
        this.toastrService.success(`Atualização enviada para a conexão ${this.connectionId}`);
        this.router.navigate(['/whatsapp/connections']);
      },
      error: () => {
        this.toastrService.error('Falha ao atualizar conexão');
      }
    }).add(() => {
      this.saving.set(false);
    });
  }
}
